import type { WorkspaceSpotlightTourStep } from '../../../shared/ui/common/WorkspaceSpotlightTour';
import { quickFillLayout } from '../../../theme/theme';
import {
  detail,
  spotlightStep,
  type QuickFillTourRefs,
} from './quickFillGuideHelpers';

export const buildValuePanelTourStep = (
  refs: QuickFillTourRefs
): WorkspaceSpotlightTourStep => spotlightStep(
  {
    id: 'value-panel',
    title: 'Use the value panel for controlled values',
    focusLabel: 'Value selection and application',
    intro: 'Use the value panel to apply predefined values to the active cell or current selection.',
    details: [
      detail('Best for', [
        'Use it when a parameter has a defined set of values and you want consistent results.',
        'It is faster and more reliable than manual entry when multiple cells should receive the same value.',
      ]),
      detail('Scope reminder', [
        'The selected value is applied to the active cell or current selection, so confirm the intended cells before choosing a value.',
      ], 'caution'),
    ],
    nextAction: 'Use the value panel when the required value is known and needs to be applied consistently.',
  },
  refs.valuePanelRef,
  {
    top: 224,
    left: Math.max(window.innerWidth - 412, 24),
    width: 388,
    height: Math.max(window.innerHeight - 316, 260),
  }
);

export const buildValueSearchTourStep = (
  refs: QuickFillTourRefs
): WorkspaceSpotlightTourStep => spotlightStep(
  {
    id: 'value-search',
    title: 'Search the value panel when the list is long',
    focusLabel: 'Value search and filtering',
    keepSpotlightClear: true,
    compactOnNarrowViewport: true,
    intro: 'Use the search box to narrow a long list of available values and find the value you want to apply.',
    relatedTargets: [
      {
        label: 'Value panel',
        targetRef: refs.valuePanelRef,
        affectsCalloutLayout: false,
      },
    ],
    details: [
      detail('How to use it', [
        'Search by part of the value instead of scrolling through the full list.',
        'Keep the intended cells selected while you narrow the list.',
      ]),
      detail('Why it matters', [
        'Filtering makes it easier to find and apply the correct value when the list is long or contains similar entries.',
      ], 'accent'),
    ],
    nextAction: 'Filter the list, confirm the correct value, then apply it to the selected cells.',
  },
  refs.valueSearchRef,
  {
    top: 246,
    left: Math.max(window.innerWidth - 388, 24),
    width: quickFillLayout.valueSpotlightWidth,
    height: 74,
  }
);
